import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { statsApi, exchangeRatesApi, speseApi } from '../lib/api';
import { BarChart3, Gauge, Wallet, Map, ChevronDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#FF5F1F', '#10B981', '#F59E0B', '#3B82F6', '#8B5CF6', '#EC4899'];

const VALUTE = [
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'CHF', symbol: 'CHF', name: 'Franco Svizzero' },
  { code: 'USD', symbol: '$', name: 'Dollaro USA' },
];

export default function Statistiche() {
  const [stats, setStats] = useState(null);
  const [spesePerCategoria, setSpesePerCategoria] = useState([]);
  const [kmPerMoto, setKmPerMoto] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCurrency, setSelectedCurrency] = useState('CHF');
  const [exchangeRates, setExchangeRates] = useState({});
  const [spese, setSpese] = useState([]);
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, speseRes, kmRes, ratesRes, allSpeseRes] = await Promise.all([
        statsApi.getStats(),
        statsApi.getSpesePerCategoria(),
        statsApi.getKmPerMoto(),
        exchangeRatesApi.getRates(),
        speseApi.getAll()
      ]);
      setStats(statsRes.data);
      setSpesePerCategoria(speseRes.data);
      setKmPerMoto(kmRes.data);
      setExchangeRates(ratesRes.data.rates || {});
      setSpese(allSpeseRes.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Converti importo da una valuta a EUR
  const convertToEur = (importo, fromCurrency) => {
    if (!fromCurrency || fromCurrency === 'EUR') return importo;
    const rate = exchangeRates[fromCurrency];
    if (!rate) return importo;
    return importo * rate;
  };

  // Converti da EUR a valuta target
  const convertFromEur = (eurAmount, toCurrency) => {
    if (!toCurrency || toCurrency === 'EUR') return eurAmount;
    const rate = exchangeRates[toCurrency];
    if (!rate || rate === 0) return eurAmount;
    return eurAmount / rate;
  };

  // Calcola totale spese nella valuta selezionata
  const getTotalInCurrency = () => {
    const totalEur = spese.reduce((total, s) => {
      const valuta = s.valuta || 'EUR';
      return total + convertToEur(s.importo || 0, valuta);
    }, 0);
    return convertFromEur(totalEur, selectedCurrency);
  };

  // Calcola spese per categoria nella valuta selezionata
  const getSpesePerCategoriaInCurrency = () => {
    return spesePerCategoria.map(cat => ({
      ...cat,
      totale: convertFromEur(cat.totale || 0, selectedCurrency)
    }));
  };

  const formatCurrency = (value, currency = selectedCurrency) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: currency
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('it-IT').format(value || 0);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage = totaleSpeseCategoria > 0 
        ? Math.round((data?.value / totaleSpeseCategoria) * 100) 
        : 0;
      return (
        <div className="bg-[#0a0a0a] border border-[#FF5F1F] p-4 rounded shadow-lg">
          <p className="text-white font-semibold text-lg">{data?.name || label}</p>
          <p className="text-[#FF5F1F] font-mono text-xl font-bold">
            {formatCurrency(data?.value)}
          </p>
          <p className="text-[#9CA3AF] text-sm">{percentage}% del totale</p>
        </div>
      );
    }
    return null;
  };

  // Custom label for pie chart - white text
  const renderCustomLabel = ({ name, percent, cx, cy, midAngle, innerRadius, outerRadius }) => {
    if (percent < 0.08) return null; // Don't show label if segment is too small
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        className="text-xs font-medium"
      >
        {name} ({(percent * 100).toFixed(0)}%)
      </text>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="page-container flex items-center justify-center min-h-[60vh]">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  const speseConverted = getSpesePerCategoriaInCurrency();
  const totaleSpeseCategoria = speseConverted.reduce((sum, s) => sum + (s.totale || 0), 0);

  return (
    <Layout>
      <div className="page-container" data-testid="statistiche-page">
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Statistiche</h1>
            <p className="page-subtitle">Report e analisi</p>
          </div>
          
          {/* Currency Selector */}
          <div className="relative">
            <button
              onClick={() => setCurrencyDropdownOpen(!currencyDropdownOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-[#121212] border border-[#262626] hover:border-[#FF5F1F]/50 text-white transition-colors"
              data-testid="stats-currency-selector"
            >
              <span className="text-[#9CA3AF] text-sm">Valuta:</span>
              <span className="font-mono">{VALUTE.find(v => v.code === selectedCurrency)?.symbol} {selectedCurrency}</span>
              <ChevronDown className={`w-4 h-4 text-[#9CA3AF] transition-transform ${currencyDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {currencyDropdownOpen && (
              <div className="absolute top-full right-0 mt-1 bg-[#0a0a0a] border border-[#262626] z-50 min-w-[120px]">
                {VALUTE.map((v) => (
                  <button
                    key={v.code}
                    onClick={() => { setSelectedCurrency(v.code); setCurrencyDropdownOpen(false); }}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-[#FF5F1F]/10 transition-colors ${
                      selectedCurrency === v.code ? 'text-[#FF5F1F] bg-[#FF5F1F]/10' : 'text-white'
                    }`}
                  >
                    {v.symbol} {v.code}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard 
            icon={Gauge} 
            label="Km Totali" 
            value={formatNumber(stats?.totale_km || 0)}
          />
          <StatCard 
            icon={Map} 
            label="Viaggi" 
            value={stats?.totale_viaggi || 0}
          />
          <StatCard 
            icon={Wallet} 
            label="Spese Totali" 
            value={formatCurrency(getTotalInCurrency())}
          />
          <StatCard 
            icon={BarChart3} 
            label="Media/Viaggio" 
            value={stats?.totale_viaggi > 0 
              ? `${formatNumber(Math.round((stats?.totale_km || 0) / stats.totale_viaggi))} km`
              : '0 km'
            }
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Spese per Categoria - Pie Chart */}
          <div className="vault-card" data-testid="spese-chart">
            <h2 className="font-['Teko'] text-2xl text-white uppercase tracking-tight mb-6">
              Spese per Categoria
            </h2>
            
            {speseConverted.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-[#9CA3AF]">
                Nessuna spesa registrata
              </div>
            ) : (
              <>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={speseConverted}
                        dataKey="totale"
                        nameKey="categoria"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        strokeWidth={2}
                        stroke="#0a0a0a"
                        label={renderCustomLabel}
                        labelLine={{ stroke: '#666', strokeWidth: 1 }}
                      >
                        {speseConverted.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={COLORS[index % COLORS.length]}
                            className="cursor-pointer hover:opacity-80 transition-opacity"
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 space-y-2">
                  {speseConverted.map((item, index) => (
                    <div key={item.categoria} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-[#9CA3AF] text-sm">{item.categoria}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-white font-mono text-sm">
                          {formatCurrency(item.totale)}
                        </span>
                        <span className="text-[#9CA3AF] text-xs w-12 text-right">
                          {totaleSpeseCategoria > 0 
                            ? `${Math.round((item.totale / totaleSpeseCategoria) * 100)}%`
                            : '0%'
                          }
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Km per Moto - Bar Chart */}
          <div className="vault-card" data-testid="km-chart">
            <h2 className="font-['Teko'] text-2xl text-white uppercase tracking-tight mb-6">
              Km per Moto
            </h2>
            
            {kmPerMoto.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-[#9CA3AF]">
                Nessuna moto registrata
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={kmPerMoto} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#262626" horizontal={false} />
                    <XAxis 
                      type="number" 
                      tick={{ fill: '#9CA3AF', fontSize: 12 }}
                      tickFormatter={(value) => formatNumber(value)}
                    />
                    <YAxis 
                      type="category" 
                      dataKey="nome" 
                      tick={{ fill: '#9CA3AF', fontSize: 12 }}
                      width={120}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="km" fill="#FF5F1F" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Additional Stats */}
        {stats && (
          <div className="mt-8 vault-card">
            <h2 className="font-['Teko'] text-2xl text-white uppercase tracking-tight mb-6">
              Riepilogo
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <p className="text-[#9CA3AF] text-xs uppercase tracking-wider mb-1">Moto nel garage</p>
                <p className="text-white font-['Teko'] text-3xl">{stats.totale_moto}</p>
              </div>
              <div>
                <p className="text-[#9CA3AF] text-xs uppercase tracking-wider mb-1">Promemoria attivi</p>
                <p className="text-white font-['Teko'] text-3xl">{stats.promemoria_attivi}</p>
              </div>
              <div>
                <p className="text-[#9CA3AF] text-xs uppercase tracking-wider mb-1">Ultima manutenzione</p>
                <p className="text-white font-mono">{stats.ultima_manutenzione || '—'}</p>
              </div>
              <div>
                <p className="text-[#9CA3AF] text-xs uppercase tracking-wider mb-1">Costo medio/viaggio</p>
                <p className="text-[#FF5F1F] font-mono text-xl">
                  {stats.totale_viaggi > 0 
                    ? formatCurrency(getTotalInCurrency() / stats.totale_viaggi)
                    : formatCurrency(0)
                  }
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="vault-card stat-card">
      <div className="flex items-center gap-3 mb-3">
        <Icon className="w-5 h-5 text-[#9CA3AF]" />
        <span className="text-[#9CA3AF] text-xs uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-[#FF5F1F] font-mono text-2xl">{value}</p>
    </div>
  );
}
